import { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client";
import {
  Group,
  Button,
  Avatar,
  Text,
  useMantineTheme,
  SelectItem,
} from "@mantine/core";
import { forwardRef } from "react";
import { ProductPage } from "src/api/graphql/queries/Product";
import Select from "src/components/Select";
import {
  DiscountCodeType,
  GetAgreementPageDocument,
  GetAgreementPageQuery,
  GetAgreementPageQueryVariables,
  ProductPageQuery,
  ProductPageQueryVariables,
} from "src/generated/graphql";
import { ArrayElement } from "src/utils/helperTypes";
import { debounce } from "src/utils/utils";
import { useAppContext } from "src/api/AppContext";

type SearchTypeList =
  | GetAgreementPageQuery["agreementPage"]["items"]
  | ProductPageQuery["productPage"]["items"];

interface SearchListProps {
  type: DiscountCodeType;
  onCancel: () => void;
  onSubmit: (values: string) => void;
  currentIdList: string[];
}

const ItemSelect = forwardRef<HTMLDivElement, SelectItem>(
  ({ image, label, description, ...others }: SelectItem, ref) => (
    <div ref={ref} {...others}>
      <Group noWrap>
        <Avatar src={image} />

        <div>
          <Text size="sm">{label}</Text>
          <Text size="xs" opacity={0.65}>
            {description}
          </Text>
        </div>
      </Group>
    </div>
  )
);

export const SearchList = ({
  type,
  onCancel,
  onSubmit,
  currentIdList,
}: SearchListProps) => {
  const { currentOrganization } = useAppContext();
  const [searchedTypeList, setSearchTypeList] = useState<SelectItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<string | null>("");
  const [searchedItem, setSearchedItem] = useState<string | null>(null);
  const theme = useMantineTheme();
  const { query } = useApolloClient();
  const onSearchList = (searchValue: string = "") => {
    if (searchValue === searchedItem) return;
    setSearchedItem(searchValue);

    const variables = {
      pagination: {
        count: 10,
        page: 1,
        filters: [
          {
            field: "name",
            type: "text",
            value: searchValue,
          },
          {
            field: "organizationId",
            type: "text",
            value: currentOrganization?.id || "",
            parentRelationField: "location",
          },
          // Only show with Active Status (Agreement & Product)
          {
            field: "isActive",
            type: "text",
            value: "1",
          },
        ],
      },
    };
    if (type === DiscountCodeType.Agreement) {
      query<GetAgreementPageQuery, GetAgreementPageQueryVariables>({
        query: GetAgreementPageDocument,
        variables,
      }).then(({ data }) => {
        setSearchTypeList(
          normalizeList(
            data.agreementPage.items.filter(
              (item) => !currentIdList.includes(item.id)
            )
          )
        );
      });
    } else {
      query<ProductPageQuery, ProductPageQueryVariables>({
        query: ProductPage,
        variables,
      }).then(({ data }) => {
        setSearchTypeList(
          normalizeList(
            data.productPage.items.filter(
              (item) => !currentIdList.includes(item.id)
            )
          )
        );
      });
    }
  };

  const normalizeList = (list: SearchTypeList): SelectItem[] => {
    if (!list.length) {
      return [];
    }
    const itemList = list.map((item) => {
      const { name, id, termValue, Unit, imgUrl, sku } = item as ArrayElement<
        GetAgreementPageQuery["agreementPage"]["items"]
      > &
        ArrayElement<ProductPageQuery["productPage"]["items"]>;
      return {
        label: name,
        value: `${name}|${id}`,
        description: sku ? sku : `${termValue} ${Unit}`,
        image: imgUrl,
      } as SelectItem;
    });
    return itemList;
  };

  // debounce hook won't work here, as we need to debounce the function call
  const onDebouncedSearch = debounce(onSearchList, 500);

  useEffect(() => {
    onSearchList();
  }, [type]);

  return (
    <>
      <Select
        data={searchedTypeList}
        placeholder={`Type To Find Desired ${type}`}
        itemComponent={ItemSelect}
        searchable
        clearable
        maxDropdownHeight={500}
        nothingFound="No result found!"
        onSearchChange={onDebouncedSearch}
        onChange={setSelectedItem}
      />
      <Group position="apart" mt="md" style={{ marginTop: 30 }}>
        <Button
          radius="md"
          variant="outline"
          onClick={onCancel}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[5],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          radius="md"
          onClick={() => onSubmit(selectedItem!)}
          disabled={!selectedItem}
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
        >
          Add
        </Button>
      </Group>
    </>
  );
};
